<template>
	<div v-show="displayValidationRunning" class="flex items-center justify-center p-4">
		<div class="text-center">
			<i class="pi pi-spin pi-spinner text-2xl mb-2"></i>
			<div>Running Validation Check...</div>
		</div>
	</div>
	<div v-show="displayCalculationRunning" class="flex items-center justify-center p-4">
		<div class="flex items-center gap-2">
			<i class="pi pi-spin pi-spinner text-2xl "></i>
			<div>Results are being calculated...</div>
		</div>
	</div>
	<div v-show="!displayValidationRunning" class="flex flex-col gap-2">
		<!-- Page Label -->
		<PageLabel helpPageName="AssessmentsLifeCycleSummaryResult">Results</PageLabel>

		<warningBlock :header="'Some required information is missing:'" :warningMessages="reportValidationErrors"></warningBlock />

		<div v-show="warningMessages.length > 0">
			<warningBlock :warningMessages="warningMessages" :header="`Calculation Warnings`" />
		</div>
		<div v-show="isAutomaticPopulationEnabled && isProductFormatDataMissing">
			<warningBlock :warningMessages="automaticPopulationWarnings" :header="`Automatic Population Active`" />
		</div>
		<FormCard v-if="reportValidationErrors.length == 0">
			<div class="flex flex-col md:flex-row justify-between gap-2">
				<div class="flex flex-col gap-2 flex-1">
					<div v-if="lifeTimeRefUnit" class="flex items-center gap-2">
						<I2Label label="Reference Unit" helpElementName="LifeCycleSummaryReferenceUnit" />
						<span>{{ lifeTimeRefUnit }}</span>
					</div>
					<div v-if="!isProductFormatDataMissing" class="flex flex-col md:flex-row gap-2 items-start md:items-center">
						<I2Label label="Drug product mass per reference unit" helpElementName="LifeCycleSummaryDrugProductMassPerReferenceUnit">
							<span class="font-medium">Drug product mass per reference unit</span>
							<span v-if="isAutomaticPopulationEnabled" class="whitespace-nowrap font-medium">inherited from <span class="font-bold">Product Process</span>:</span>
							<span v-else class="whitespace-nowrap font-medium">:</span>
							<div class="flex gap-2 items-center">
								<div v-if="isAutomaticPopulationEnabled" class="flex gap-2 items-start">
									<div class="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
										<span class="text-xl font-bold text-blue-700">
											{{ drugProductMassPerReferenceUnit }}
										</span>
										<span class="text-base font-semibold text-blue-600  tracking-wide">
											{{ selectedUnitTypeName }}
										</span>
									</div>
									<!-- <i
										class="pi pi-info-circle text-blue-600 cursor-help"
										v-tooltip.top="{
											value: 'Drug product mass and unit are now automatically populated based on the Product Format, Mass per Product Format, and Unit fields in General Information of the \'Product Process\' module.',
											class: 'custom-tooltip-wide'
										}"
									></i> -->
								</div>
								<div v-else class="flex gap-2 items-center">
									<InputText
										:modelValue="drugProductMassPerReferenceUnit?.toString() || ''"
										@update:modelValue="drugProductMassPerReferenceUnit = $event ? parseFloat($event) : null"
										v-on:keypress="numbersOnly"
										:class="{ 'p-invalid': searchAttempted && !drugProductMassPerReferenceUnit }"
										maxlength="10"
										class="w-20"
									/>
									<Select
										v-model="selectedUnitType"
										:options="unitList"
										optionLabel="unitName"
										optionValue="unitName"
										placeholder="Select Unit"
										:class="{
											'p-invalid': searchAttempted && (selectedUnitType == null || selectedUnitType == '' || selectedUnitType == 'Select Unit'),
										}"
										class="w-32"
									/>
								</div>
							</div>
						</I2Label>
					</div>
			<div v-if="displayLifeCycleStage" class="mt-6">
				<div class="mb-4">
					<h3 class="text-lg font-semibold text-gray-800">Lifecycle results - per {{ productFormatName }}</h3>

				</div>
				<ResultGridFilterBadge :visible-count="visibleRows.length" :total-count="filteredLifeCycleResultList.length" :is-filtered="isFiltered" @clear="clearAllFilters" />
				<br>
				<div class="flex">
					<ag-grid-vue
						:theme="resultGridTheme"
						style="width: 100%; min-height: 350px"
						:rowData="filteredLifeCycleResultList"
						:columnDefs="agGridColumnDefs"
						:loadingOverlayComponentParams="{ loadingMessage: 'Loading life cycle results. Please wait.' }"
						:noRowsOverlayComponentParams="{ noRowsMessageFunc: () => 'No life cycle results found.' }"
						overlayLoadingTemplate='<span class="ag-overlay-loading-center">Loading life cycle results. Please wait.</span>'
						overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No life cycle results found.</span>'
						:context="{}"
						:loadThemeGoogleFonts="true"
						:defaultColDef="resultGridDefaultColDef"
						:pinnedBottomRowData="pinnedBottomRow"
						:loading="loading1"
						@grid-ready="onGridReady"
						@row-data-updated="refreshVisibleRows"
						@model-updated="refreshVisibleRows"
						@filter-changed="refreshVisibleRows"
						@sort-changed="refreshVisibleRows"
						@first-data-rendered="refreshVisibleRows"
					>
					</ag-grid-vue>
				</div>

				<!-- Chart Type Toggle -->
				<div class="flex mt-2 mb-2 justify-center">
					<div class="flex items-center space-x-4">
						<span class="text-sm font-medium" :class="{ 'text-blue-600': !showPieChart, 'text-gray-500': showPieChart }">Bar Chart</span>
						<label class="relative inline-flex items-center cursor-pointer">
							<input type="checkbox" v-model="showPieChart" class="sr-only peer" />
							<div
								class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"
							></div>
						</label>
						<span class="text-sm font-medium" :class="{ 'text-blue-600': showPieChart, 'text-gray-500': !showPieChart }">Pie Chart</span>
						<label v-show="!showPieChart" v-tooltip.top="'Display charts in a scrollable container with fixed height. Useful when there are many items.'" class="flex items-center gap-1 text-sm text-gray-500 cursor-pointer select-none ml-4">
							<input type="checkbox" v-model="scrollMode" class="accent-blue-600" />
							Scroll
				</label>
				<label v-show="!showPieChart" v-tooltip.top="'Widen each chart (fewer charts per row) for a clearer visualization.'" class="flex items-center gap-1 text-sm text-gray-500 cursor-pointer select-none ml-2">
					<input type="checkbox" v-model="extendMode" class="accent-blue-600" />
					Extend
						</label>
					</div>
				</div>
				<!-- Dynamic Charts Component with CSS Grid Layout -->
				<div :class="['grid gap-4 mt-4', extendMode ? 'grid-cols-1 xl:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3']" v-if="lifeCycleResultList && lifeCycleResultList.length > 0">
					<DynamicChartComponent
						v-for="emissionFactor in activeEmissionFactors"
						:key="emissionFactor.emissionFactorName"
						:title="isWeighted ? emissionFactor.emissionFactorName?.replace(/ \[.*\]$/, ' [mPt]') : emissionFactor.emissionFactorName"
						:emissionFactors="emissionFactor"
						:chartData="chartDataItems"
						:showPieChart="showPieChart"
						:scrollMode="scrollMode"
						class="min-h-96 flex flex-col"
					/>
				</div>
				</div>
			</div>

			<!-- Colonna destra: Buttons + SelectableList -->
			<div class="flex flex-col gap-4" style="min-width: 400px; max-width: 450px">
				<!-- Buttons -->
				<div class="flex flex-row items-center gap-2 justify-end">
					<Button
						class="btn p-button"
						:disabled="isAutomaticPopulationEnabled && isProductFormatDataMissing"
						@click="generateLifeCycleSummaryResult"
					>Calculate</Button>

					<Button
						class="btn p-button"
						:disabled="!displayStepList || reportsStore.selectedEmissionFactorNames.length === 0"
						v-if="reportValidationErrors.length == 0"
						@click="exportCalculationLog()"
						v-tooltip.top="reportsStore.selectedEmissionFactorNames.length === 0
							? 'Please select at least one emission factor to export'
							: 'Export calculation log to Excel'">
						Log
					</Button>

					<Button
						class="btn p-button"
						:disabled="!displayStepList || reportsStore.selectedEmissionFactorNames.length === 0"
						v-if="reportValidationErrors.length == 0"
						@click="exportToBody()"
						v-tooltip.top="reportsStore.selectedEmissionFactorNames.length === 0
							? 'Please select at least one emission factor to export'
							: 'Export results to Excel'">
						Export
					</Button>
				</div>

				<!-- Packaging and Distribution Option Selectors -->
				<div class="flex flex-col gap-2" v-if="isMultiOptionFeatureEnabled && (packagingOptions.length > 1 || distributionOptions.length > 1)">
					<div v-if="packagingOptions.length > 1" class="flex flex-col gap-1">
						<I2Label label="Device & Packaging Option" helpElementName="LifeCycleSummaryPackagingOption" />
						<Select
							v-model="selectedPackagingOptionId"
							:options="packagingOptions"
							optionLabel="generalInformationName"
							optionValue="id"
							placeholder="Select Packaging Option"
							class="w-full"
							@change="onLifeCycleOptionSelectorChange"
						/>
					</div>
					<div v-if="distributionOptions.length > 1" class="flex flex-col gap-1">
						<I2Label label="Distribution Option" helpElementName="LifeCycleSummaryDistributionOption" />
						<Select
							v-model="selectedDistributionOptionId"
							:options="distributionOptions"
							optionLabel="generalInformationName"
							optionValue="id"
							placeholder="Select Distribution Option"
							class="w-full"
							@change="onLifeCycleOptionSelectorChange"
						/>
					</div>
				</div>

				<!-- Weighted Toggle -->
				<div v-if="weightedOptionEnabled" class="flex items-center gap-2">
					<ToggleSwitch v-model="isWeighted" inputId="weightedToggle" />
					<I2Label label="Weighted" helpElementName="LifeCycleSummaryWeightedToggle" />
				</div>

				<!-- SelectableList -->
				<SelectableList
					v-model="reportsStore.selectedEmissionFactorNames"
					:options="reportsStore.emissionFactors"
					option-label="emissionFactorName"
					option-value="emissionFactorName"
					placeholder="Select Emission Factors"
					:filter="true"
					:preserve-option-order="true"
				/>
			</div>
		</div>
	</FormCard>
	</div>
</template>
<script setup lang="ts">
import { ref, onMounted, onActivated, computed, effect, watch } from "vue"
import Select from "primevue/select"
import MultiSelect from "primevue/multiselect"
import InputText from "primevue/inputtext"
import Button from "primevue/button"
import FormCard from "@/crispify/crispy-components/FormCard.vue"
import SelectableList from "./SelectableList.vue"
import PageLabel from "./pageLabel.vue"
import {
	ApiClient,
	FeatureFlagClient,
	GeneralInformation,
	Step,
	LifeCycleSummaryResultInput,
	Unit,
	ProductProcessLifeCycleResult,
	LifeCycleSummaryResultGraph,
	AllResultsOther,
	ExportRequestOther,
	FileResponse,
} from "../../entities/NSwagGenerated"
import { useToast } from "primevue/usetoast"
import { EventBus } from "@/eventBus"
import DataTable from "primevue/datatable"
import Column from "primevue/column"
import Row from "primevue/row"
import ColumnGroup from "primevue/columngroup"
import ToggleSwitch from "primevue/toggleswitch"
import { library } from "@fortawesome/fontawesome-svg-core"
import { faInfoCircle, faTrash, faPen, faFloppyDisk, faXmarkLarge, faWarning, faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import DynamicChartComponent from "./DynamicChartComponent.vue"
import type { ChartDataItem } from "./DynamicChartComponent.vue"
import ResultGridFilterBadge from "./ResultGridFilterBadge.vue"
import { useGridFilterSync } from "../composables/useGridFilterSync"
import Chart from "primevue/chart"
import { useReportsStore } from "@/store/reportsStore"

library.add(faTrash, faPen, faInfoCircle, faFloppyDisk, faXmarkLarge, faWarning, faTriangleExclamation)

import I2FormField from "@/crispify/crispy-components/I2FormField.vue"
import I2Label from "@/crispify/crispy-components/I2Label.vue"
import { uiNumberFormatterAZ } from "../composables/AZGlobalFunctions"
import { useErrorToaster } from "@/composables/toaster"
import warningBlock from "./warningBlock.vue"
import { AgGridVue } from "ag-grid-vue3"
import { ColDef, CellStyle, GridApi, GridReadyEvent, ValueGetterParams, ValueFormatterParams } from "ag-grid-community"
import {
	getConditionalFormatColor,
	valueToStringFormatter,
	resultGridTheme,
	resultGridDefaultColDef,
	textFilterColDef,
	numberFilterColDef,
	BuildMinMaxValuesFromEmissionFactors,
	ColumnMinMax,
} from "../composables/resultGridHelper"
import { useTabsDefinitions } from "../composables/tabsDefinitions"
const tabsDefinitions = useTabsDefinitions()
const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const featureFlagClient = new FeatureFlagClient(import.meta.env.VITE_API_URL?.toString())

interface IVersionList {
	versionNumber: number
	versionName: string
}

const props = defineProps({
	assessmentId: {
		type: String,
		required: false,
		default: "",
	},
	tabId: {
		type: String,
		required: false,
		default: "",
	},
	tabName: {
		type: String,
		required: false,
		default: "",
	},
})

const selectedVersion = ref(0)
const screenId = ref()
const generalInformation = ref(GeneralInformation)
const generalInformationId = ref()
let toast = useToast()
const loading1 = ref(false)
const displayLifeCycleStage = ref(false)
const displayStepList = ref(false)
const displayLifeCycleResultListGraph1 = ref(false)
const nextPrevEnabled = ref(false)
const selectedImpactCategory = ref("")
const displayUnit = ref(false)
const displayValidationRunning = ref(false)
const displayCalculationRunning = ref(false)
const warningMessages = ref([] as string[])
const isWeighted = ref(false)
const weightedOptionEnabled = ref(false)

const lifeCycleResultList = ref([] as ProductProcessLifeCycleResult[])
const lifeCycleResultListGraph1 = ref([] as LifeCycleSummaryResultGraph[])
const generalInformationList = ref([] as GeneralInformation[])
const steps = ref([] as Step[])
const impactCategoryList = ["Climate change", "Freshwater use", "PMI"]
const resultKey = ref(0)
const showPieChart = ref(true)
const scrollMode = ref(false)
const extendMode = ref(false)

const lifeTimeRefUnit = ref()
const drugProductMassPerReferenceUnit = ref<number | null>(null)
const unitList = ref([] as Unit[])
const selectedUnitType = ref("")
const selectedUnitTypeName = ref("")
const searchAttempted = ref(false)
const productFormatName = ref<string | undefined>(undefined)

// Feature flag for automatic population
const isAutomaticPopulationEnabled = ref(false)
const automaticPopulationWarnings = ref([] as string[])
const currentGeneralInformation = ref<GeneralInformation | null>(null)
const hasProductProcessTab = ref(false)
const isProductFormatDataMissing = ref(false)

const reportValidationErrors = ref(new Array<string>())
const reportsStore = useReportsStore()

// Packaging and Distribution options for Life Cycle Summary
const packagingOptions = ref<GeneralInformation[]>([])
const distributionOptions = ref<GeneralInformation[]>([])
const selectedPackagingOptionId = ref<string | null>(null)
const selectedDistributionOptionId = ref<string | null>(null)
const packagingScreenId = "62cdd11a-f63f-49bc-8282-898e5e8f7334"
const distributionScreenId = "a69c4ff1-1508-46de-9c33-2247716da798"
const isMultiOptionFeatureEnabled = ref(false)

// Missing variable declarations for conditional formatting
const climageChangeMax = ref<number>(0)
const climageChangeLow = ref<number>(0)
const climageChangeMedium = ref<number>(0)
const waterConsumptionMax = ref<number>(0)
const waterConsumptionLow = ref<number>(0)
const waterConsumptionMedium = ref<number>(0)
const costofGoodsMax = ref<number>(0)
const costofGoodsLow = ref<number>(0)
const costofGoodsMedium = ref<number>(0)
const waterMax = ref<number>(0)
const waterLow = ref<number>(0)
const waterMedium = ref<number>(0)
const solventMax = ref<number>(0)
const solventLow = ref<number>(0)
const solventMedium = ref<number>(0)
const pmiMax = ref<number>(0)
const pmiLow = ref<number>(0)
const pmiMedium = ref<number>(0)

// AG-Grid setup
const gridApi = ref<GridApi | null>(null)
const columnMinMax = ref<Array<ColumnMinMax>>([])

function onGridReady(params: GridReadyEvent) {
	console.log("AG-Grid ready:", params)
	gridApi.value = params.api
	refreshVisibleRows()
	// Debug grid container dimensions
	setTimeout(() => {
		const gridContainer = document.querySelector(".ag-theme-container .ag-root-wrapper")
		if (gridContainer) {
			console.log("Grid container found:", gridContainer)
			console.log("Grid container dimensions:", {
				width: gridContainer.clientWidth,
				height: gridContainer.clientHeight,
				offsetWidth: gridContainer.offsetWidth,
				offsetHeight: gridContainer.offsetHeight,
			})
		} else {
			console.log("Grid container NOT found")
		}
	}, 100)
}

// Computed property to filter out Total row for display in grid (Total will be shown in pinned row)
const filteredLifeCycleResultList = computed(() => lifeCycleResultList.value.filter((row) => row.name !== "Total"))

// Rows currently visible in the grid after filtering - drives the charts and the grand total row.
// Declared before any eager watch that reads chartDataItems, to avoid a temporal dead zone error.
const { visibleRows, isFiltered, refreshVisibleRows, clearAllFilters } = useGridFilterSync<ProductProcessLifeCycleResult>(
	gridApi,
	filteredLifeCycleResultList
)

const agGridColumnDefs = computed((): ColDef[] => [
	{ field: "name", headerName: "Life Cycle Stage", pinned: "left", ...textFilterColDef },
	...(isWeighted.value
		? [
				{
					field: "totalWeightedScore",
					headerName: "Total Weighted Score [mPt]",
					...numberFilterColDef,
					valueGetter: (p: ValueGetterParams) => {
						return p.data?.totalWeightedScore ?? null
					},
					valueFormatter: (p: ValueFormatterParams) => valueToStringFormatter(p.value),
					aggFunc: "sum",
				},
		  ]
		: []),
	...reportsStore.emissionFactors.filter(ef => reportsStore.selectedEmissionFactorNames.some(s => ef.emissionFactorName === s)).map<ColDef>(ef => ({
		field: ef.emissionFactorName,
		headerName: isWeighted.value ? ef.emissionFactorName?.replace(/ \[.*\]$/, " [mPt]") : ef.emissionFactorName,
		...numberFilterColDef,
		valueGetter: (p) => {
			const value = ef.emissionFactorName == null || !p.data?.emissionFactors ? null : p.data?.emissionFactors[ef.emissionFactorName] ?? null
			return value
		},
		valueFormatter: (p) => valueToStringFormatter(p.value),
		cellStyle: (params): CellStyle | null => {
			// Don't apply conditional formatting to pinned rows
			if (params.node.rowPinned) {
				return { "text-align": "right" }
			}
			let minMax = columnMinMax.value.find((item) => item.columnName === ef.emissionFactorName)
			if (minMax) {
				return {
					"background-color": getConditionalFormatColor(params.value, minMax.min, minMax.max),
					"text-align": "right",
				}
			}
			return { "text-align": "right" }
		},
		aggFunc: "sum",
	}))
])

// Computed property to parse doseStrength from generalInformation (Product Process)
const parsedDoseStrength = computed(() => {
	if (!currentGeneralInformation.value?.doseStrength) {
		return { value: null, unit: null }
	}

	const doseStr = currentGeneralInformation.value.doseStrength.trim()
	// Match patterns like "500 mg", "1.5 g", "10mg", etc.
	const match = doseStr.match(/^([\d.]+)\s*([a-zA-Z]+)$/)

	if (match) {
		return {
			value: parseFloat(match[1]),
			unit: match[2],
		}
	}

	return { value: null, unit: null }
})

// Computed property for pinned bottom row (Total)
const pinnedBottomRow = computed(() => {
	if (!visibleRows.value.length) return []

	const totals = {
		name: "Grand Total",
		emissionFactors: {} as { [key: string]: number },
		totalWeightedScore: 0,
	}

	// Sum the rows currently visible in the grid, so the Grand Total reflects any active filter.
	for (const row of visibleRows.value) {
		if (row.emissionFactors) {
			for (const ef of reportsStore.emissionFactors.filter((ef) =>
				reportsStore.selectedEmissionFactorNames.some((s) => ef.emissionFactorName === s)
			)) {
				if (!ef.emissionFactorName) continue
				totals.emissionFactors[ef.emissionFactorName] =
					(totals.emissionFactors[ef.emissionFactorName] || 0) + (row.emissionFactors[ef.emissionFactorName] || 0)
			}
		}
		totals.totalWeightedScore += row.totalWeightedScore || 0
	}
	return [totals]
})

// Computed property for active emission factors (filtered by selection)
const activeEmissionFactors = computed(() => {
	return reportsStore.selectedEmissionFactors
})

// Computed property to map lifeCycleResultList to ChartDataItem format (excluding Total)
const chartDataItems = computed((): ChartDataItem[] => {
	return visibleRows.value.map((item) => ({
		name: item.name || "Unknown",
		value: item.climateChange ?? 0, // Use climateChange as primary value
		unit: "kg CO2 eq",
		emissionFactors: item.emissionFactors || {},
	}))
})
watch(() => chartDataItems.value.length, (newLen) => {
	scrollMode.value = newLen > 7
})

EventBus.on("deviceAndPackagingSaved", () => {
	loadBasicInfo()
	getAllUnit()
})

onMounted(async () => {
	// Check if weighted calculation option is enabled
	const weightedFlag = await featureFlagClient.getByName("Weighted Calculation Option")
	weightedOptionEnabled.value = weightedFlag?.isEnabled || false
	
	EventBus.on("lifeCycleSummaryUpdate" + props.tabId, async (data: any) => {
		checkIfCanBeCalculated()
		// Reload packaging and distribution options when tab is selected
		await loadPackagingAndDistributionOptions()
	})

	getAllUnit()
	// Load packaging and distribution options
	await loadPackagingAndDistributionOptions()
	// Check for Product Process tab first, then load feature flag and basic info
	await checkSubprojectHasProductProcess()
	loadFeatureFlag().then(() => {
		loadBasicInfo()
	})
})

// Reload options when component is reactivated (keep-alive)
onActivated(async () => {
	await loadPackagingAndDistributionOptions()
})

const checkIfCanBeCalculated = () => {
	let obj = new LifeCycleSummaryResultInput()
	obj.assessmentId = props.assessmentId
	obj.unitValue = drugProductMassPerReferenceUnit.value ?? undefined
	obj.unitType = selectedUnitType.value
	obj.packagingGeneralInformationId = selectedPackagingOptionId.value ?? undefined
	obj.distributionGeneralInformationId = selectedDistributionOptionId.value ?? undefined
	apiClient.checkIfLifeCycleSummaryCanBeCalculated(obj).then(
		(errorList) => {
			reportValidationErrors.value = errorList
		},
		(err) => {
			useErrorToaster(err)
		}
	)
}

// Re-validate and, if results are already displayed, recalculate this summary when the user picks a
// different Packaging or Distribution option. These are local, view-only selectors (not the globally
// active option), so this intentionally only re-runs this summary's own calculation rather than the
// shared option-switch flow used elsewhere in the assessment wizard.
const onLifeCycleOptionSelectorChange = () => {
	checkIfCanBeCalculated()
	if (displayLifeCycleStage.value) {
		getLifeCycleSummaryResult()
	}
}

// Load packaging and distribution generalInformation options
const loadPackagingAndDistributionOptions = async () => {
	try {
		// Load packaging options
		const packagingResult = await apiClient.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, packagingScreenId)
		packagingOptions.value = packagingResult.filter((gi) => gi.isActive === true)
		// Select the option with wasSelected = true, or fall back to the first option
		if (packagingOptions.value.length > 0) {
			const selectedOption = packagingOptions.value.find((gi) => gi.wasSelected === true) || packagingOptions.value[0]
			selectedPackagingOptionId.value = selectedOption.id ?? null
		}

		// Load distribution options
		const distributionResult = await apiClient.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, distributionScreenId)
		distributionOptions.value = distributionResult.filter((gi) => gi.isActive === true)
		// Select the option with wasSelected = true, or fall back to the first option
		if (distributionOptions.value.length > 0) {
			const selectedOption = distributionOptions.value.find((gi) => gi.wasSelected === true) || distributionOptions.value[0]
			selectedDistributionOptionId.value = selectedOption.id ?? null
		}
	} catch (error) {
		console.error("Error loading packaging and distribution options:", error)
	}
}

async function exportToBody() {
	// Validazione
	if (reportsStore.selectedEmissionFactorNames.length === 0) {
		toast.add({
			severity: "warn",
			summary: "Warning",
			detail: "Please select at least one emission factor to export",
			life: 5000,
		})
		return
	}

	try {
		const assessment = await apiClient.getSingleAssessmentById(props.assessmentId)

		var finalResultsss = new AllResultsOther()
		finalResultsss.productProcessLifeCycleResults = lifeCycleResultList.value

		// Use API client instead of axios with new payload structure
		const exportRequest = new ExportRequestOther({
			allResults: finalResultsss,
			emissionFactorNames: reportsStore.selectedEmissionFactorNames,
			isWeighted: isWeighted.value, // lifecycle summary has no weighted toggle, always use false
			assessmentId: props.assessmentId,
			packagingGeneralInformationId: selectedPackagingOptionId.value ?? undefined,
			distributionGeneralInformationId: selectedDistributionOptionId.value ?? undefined,
		})

		const response = await apiClient.downloadReportOthers(props.tabId, exportRequest)

		// Download file - response is a FileResponse with data: Blob
		const blob = response.data
		const fileURL = window.URL.createObjectURL(blob)
		const fileLink = document.createElement("a")
		fileLink.href = fileURL

		// Use the fileName from the response if available, otherwise use the generated one
		const fileName = response.fileName || `${props.tabName}_${assessment.assessmentName}_${Date.now()}.xlsx`
		fileLink.setAttribute("download", fileName)
		document.body.appendChild(fileLink)
		fileLink.click()

		// Cleanup
		window.URL.revokeObjectURL(fileURL)
		document.body.removeChild(fileLink)

		toast.add({
			severity: "success",
			summary: "Success",
			detail: "Report exported successfully",
			life: 3000,
		})
	} catch (err: any) {
		console.error("Export error:", err)
		useErrorToaster(err)
	}
}

// Build the input object used for both calculating the life cycle summary result and exporting the calculation log
function buildLifeCycleSummaryResultInput(): LifeCycleSummaryResultInput {
	let obj = new LifeCycleSummaryResultInput()
	obj.assessmentId = props.assessmentId
	obj.unitValue = drugProductMassPerReferenceUnit.value ?? undefined
	obj.unitType = selectedUnitTypeName.value
	obj.emissionFactorsToUse = reportsStore.selectedEmissionFactorNames
	obj.useWeightedEmissionFactors = isWeighted.value
	// Pass selected packaging and distribution options
	obj.packagingGeneralInformationId = selectedPackagingOptionId.value ?? undefined
	obj.distributionGeneralInformationId = selectedDistributionOptionId.value ?? undefined
	return obj
}

function exportCalculationLog() {
	const obj = buildLifeCycleSummaryResultInput()

	apiClient.getLifeCycleSummaryCalculationLog(obj).then(
		(res: FileResponse) => {
			const fileURL = window.URL.createObjectURL(new Blob([res.data]))
			const fileLink = document.createElement("a")
			fileLink.href = fileURL
			fileLink.setAttribute("download", "LifeCycleSummary_Calculation_Log.xlsx")
			document.body.appendChild(fileLink)
			fileLink.click()

			window.URL.revokeObjectURL(fileURL)
			document.body.removeChild(fileLink)
		},
		(err) => {
			useErrorToaster(err)
		}
	)
}

function getLifeCycleSummaryResult() {
	let obj = buildLifeCycleSummaryResultInput()
	lifeCycleResultList.value = []

	if (obj != null) {
		apiClient
			.getLifeCycleSummaryResult(obj)
			.then(
				(res) => {
					console.log("API Response:", res)
					displayCalculationRunning.value = false
					if (res && res != null && res.length > 0) {
						console.log("Setting lifeCycleResultList with:", res.length, "items")
						lifeCycleResultList.value = res
						displayStepList.value = true
						displayLifeCycleStage.value = true
						console.log("displayLifeCycleStage set to:", displayLifeCycleStage.value)
						console.log("Raw data received:", res)
						console.log("filteredLifeCycleResultList:", filteredLifeCycleResultList.value)
						console.log("pinnedBottomRow:", pinnedBottomRow.value)

						// Build column min/max values for conditional formatting
						const emissionFactorsData = lifeCycleResultList.value.map((item) => item.emissionFactors ?? {})
						columnMinMax.value = BuildMinMaxValuesFromEmissionFactors(emissionFactorsData)

						//create graph 1
						let climateChangeRow = new LifeCycleSummaryResultGraph()
						let waterConsumptionRow = new LifeCycleSummaryResultGraph()
						let costOfGoodsRow = new LifeCycleSummaryResultGraph()
						let pmiRow = new LifeCycleSummaryResultGraph()

						//graph1 total
						// let climateChangePercentage = 0
						// let waterConsumptionPercentage = 0
						// let costOfGoodsPercentage = 0
						// let pmiPercentage = 0
						let climateChangeTotal = 0.0
						let waterConsumptionTotal = 0.0
						let costOfGoodsTotal = 0.0
						let pmiTotal = 0.0

						res.forEach((item) => {
							if (item.name == "Total") {
								climateChangeTotal = item.climateChange
								waterConsumptionTotal = item.waterConsumption
								costOfGoodsTotal = item.costofGoods
								pmiTotal = item.total
							}
							//color
							else {
								if (item.climateChange != null && item.climateChange > climageChangeMax.value) {
									climageChangeMax.value = item.climateChange
								}
								if (item.waterConsumption != null && item.waterConsumption > waterConsumptionMax.value) {
									waterConsumptionMax.value = item.waterConsumption
								}
								if (item.costofGoods != null && item.costofGoods > costofGoodsMax.value) {
									costofGoodsMax.value = item.costofGoods
								}
								if (item.water != null && item.water > waterMax.value) {
									waterMax.value = item.water
								}
								if (item.solvent != null && item.solvent > solventMax.value) {
									solventMax.value = item.solvent
								}
								if (item.total != null && item.total > pmiMax.value) {
									pmiMax.value = item.total
								}
							}
						})

						//color
						if (climageChangeMax.value != null && climageChangeMax.value > 0) {
							climageChangeLow.value = climageChangeMax.value / 3
							climageChangeMedium.value = (climageChangeMax.value / 3) * 2
						}
						if (waterConsumptionMax.value != null && waterConsumptionMax.value > 0) {
							waterConsumptionLow.value = waterConsumptionMax.value / 3
							waterConsumptionMedium.value = (waterConsumptionMax.value / 3) * 2
						}
						if (costofGoodsMax.value != null && costofGoodsMax.value > 0) {
							costofGoodsLow.value = costofGoodsMax.value / 3
							costofGoodsMedium.value = (costofGoodsMax.value / 3) * 2
						}
						if (waterMax.value != null && waterMax.value > 0) {
							waterLow.value = waterMax.value / 3
							waterMedium.value = (waterMax.value / 3) * 2
						}
						if (solventMax.value != null && solventMax.value > 0) {
							solventLow.value = solventMax.value / 3
							solventMedium.value = (solventMax.value / 3) * 2
						}
						if (pmiMax.value != null && pmiMax.value > 0) {
							pmiLow.value = pmiMax.value / 3
							pmiMedium.value = (pmiMax.value / 3) * 2
						}

						climateChangeRow.name = "Climate change"
						waterConsumptionRow.name = "Freshwater use"
						costOfGoodsRow.name = "Cost of Goods"
						pmiRow.name = "PMI"

						res.forEach((item) => {
							if (item.name == "Synthetic API") {
								let climateChangePercentage = 0
								let waterConsumptionPercentage = 0
								let costOfGoodsPercentage = 0
								let pmiPercentage = 0

								climateChangeRow.syntheticAPI = item.climateChange
								waterConsumptionRow.syntheticAPI = item.waterConsumption
								costOfGoodsRow.syntheticAPI = item.costofGoods
								pmiRow.syntheticAPI = item.total
								//calculate percentage
								if (item.climateChange != null && item.climateChange > 0 && climateChangeTotal != null && climateChangeTotal > 0) {
									climateChangePercentage = item.climateChange / climateChangeTotal
									// if(climateChangePercentage != null && climateChangePercentage > 14)
									// {
									// 	climateChangePercentage = 14
									// }
								}
								if (item.waterConsumption != null && item.waterConsumption > 0 && waterConsumptionTotal != null && waterConsumptionTotal > 0) {
									waterConsumptionPercentage = item.waterConsumption / waterConsumptionTotal
									// if(waterConsumptionPercentage != null && waterConsumptionPercentage > 14)
									// {
									// 	waterConsumptionPercentage = 14
									// }
								}
								if (item.costofGoods != null && item.costofGoods > 0 && costOfGoodsTotal != null && costOfGoodsTotal > 0) {
									costOfGoodsPercentage = item.costofGoods / costOfGoodsTotal
									// if(costOfGoodsPercentage != null && costOfGoodsPercentage > 14)
									// {
									// 	costOfGoodsPercentage = 14
									// }
								}
								if (item.total != null && item.total > 0 && pmiTotal != null && pmiTotal > 0) {
									pmiPercentage = item.total / pmiTotal
									// if(pmiPercentage != null && pmiPercentage > 14)
									// {
									// 	pmiPercentage = 14
									// }
								}
							} else if (item.name == "Biologics API") {
								let climateChangePercentage = 0
								let waterConsumptionPercentage = 0
								let costOfGoodsPercentage = 0
								let pmiPercentage = 0

								climateChangeRow.biologicsAPI = item.climateChange
								waterConsumptionRow.biologicsAPI = item.waterConsumption
								costOfGoodsRow.biologicsAPI = item.costofGoods
								pmiRow.biologicsAPI = item.total
								//calculate percentage
								if (item.climateChange != null && item.climateChange > 0 && climateChangeTotal != null && climateChangeTotal > 0) {
									climateChangePercentage = item.climateChange / climateChangeTotal
									// if(climateChangePercentage != null && climateChangePercentage > 14)
									// {
									// 	climateChangePercentage = 14
									// }
								}
								if (item.waterConsumption != null && item.waterConsumption > 0 && waterConsumptionTotal != null && waterConsumptionTotal > 0) {
									waterConsumptionPercentage = item.waterConsumption / waterConsumptionTotal
									// if(waterConsumptionPercentage != null && waterConsumptionPercentage > 14)
									// {
									// 	waterConsumptionPercentage = 14
									// }
								}
								if (item.costofGoods != null && item.costofGoods > 0 && costOfGoodsTotal != null && costOfGoodsTotal > 0) {
									costOfGoodsPercentage = item.costofGoods / costOfGoodsTotal
									// if(costOfGoodsPercentage != null && costOfGoodsPercentage > 14)
									// {
									// 	costOfGoodsPercentage = 14
									// }
								}
								if (item.total != null && item.total > 0 && pmiTotal != null && pmiTotal > 0) {
									pmiPercentage = item.total / pmiTotal
									// if(pmiPercentage != null && pmiPercentage > 14)
									// {
									// 	pmiPercentage = 14
									// }
								}
							} else if (item.name == "Other API") {
								let climateChangePercentage = 0
								let waterConsumptionPercentage = 0
								let costOfGoodsPercentage = 0
								let pmiPercentage = 0

								climateChangeRow.otherAPI = item.climateChange
								waterConsumptionRow.otherAPI = item.waterConsumption
								costOfGoodsRow.otherAPI = item.costofGoods
								pmiRow.otherAPI = item.total
								//calculate percentage
								if (item.climateChange != null && item.climateChange > 0 && climateChangeTotal != null && climateChangeTotal > 0) {
									climateChangePercentage = item.climateChange / climateChangeTotal
									// if(climateChangePercentage != null && climateChangePercentage > 14)
									// {
									// 	climateChangePercentage = 14
									// }
								}
								if (item.waterConsumption != null && item.waterConsumption > 0 && waterConsumptionTotal != null && waterConsumptionTotal > 0) {
									waterConsumptionPercentage = item.waterConsumption / waterConsumptionTotal
									// if(waterConsumptionPercentage != null && waterConsumptionPercentage > 14)
									// {
									// 	waterConsumptionPercentage = 14
									// }
								}
								if (item.costofGoods != null && item.costofGoods > 0 && costOfGoodsTotal != null && costOfGoodsTotal > 0) {
									costOfGoodsPercentage = item.costofGoods / costOfGoodsTotal
									// if(costOfGoodsPercentage != null && costOfGoodsPercentage > 14)
									// {
									// 	costOfGoodsPercentage = 14
									// }
								}
								if (item.total != null && item.total > 0 && pmiTotal != null && pmiTotal > 0) {
									pmiPercentage = item.total / pmiTotal
									// if(pmiPercentage != null && pmiPercentage > 14)
									// {
									// 	pmiPercentage = 14
									// }
								}
							} else if (item.name == "Product Process") {
								let climateChangePercentage = 0
								let waterConsumptionPercentage = 0
								let costOfGoodsPercentage = 0
								let pmiPercentage = 0

								climateChangeRow.productProcess = item.climateChange
								waterConsumptionRow.productProcess = item.waterConsumption
								costOfGoodsRow.productProcess = item.costofGoods
								pmiRow.productProcess = item.total
								//calculate percentage
								if (item.climateChange != null && item.climateChange > 0 && climateChangeTotal != null && climateChangeTotal > 0) {
									climateChangePercentage = item.climateChange / climateChangeTotal
									// if(climateChangePercentage != null && climateChangePercentage > 14)
									// {
									// 	climateChangePercentage = 14
									// }
								}
								if (item.waterConsumption != null && item.waterConsumption > 0 && waterConsumptionTotal != null && waterConsumptionTotal > 0) {
									waterConsumptionPercentage = item.waterConsumption / waterConsumptionTotal
									// if(waterConsumptionPercentage != null && waterConsumptionPercentage > 14)
									// {
									// 	waterConsumptionPercentage = 14
									// }
								}
								if (item.costofGoods != null && item.costofGoods > 0 && costOfGoodsTotal != null && costOfGoodsTotal > 0) {
									costOfGoodsPercentage = item.costofGoods / costOfGoodsTotal
									// if(costOfGoodsPercentage != null && costOfGoodsPercentage > 14)
									// {
									// 	costOfGoodsPercentage = 14
									// }
								}
								if (item.total != null && item.total > 0 && pmiTotal != null && pmiTotal > 0) {
									pmiPercentage = item.total / pmiTotal
									// if(pmiPercentage != null && pmiPercentage > 14)
									// {
									// 	pmiPercentage = 14
									// }
								}
							} else if (item.name == "Packaging") {
								let climateChangePercentage = 0
								let waterConsumptionPercentage = 0
								let costOfGoodsPercentage = 0
								let pmiPercentage = 0

								climateChangeRow.packaging = item.climateChange
								waterConsumptionRow.packaging = item.waterConsumption
								costOfGoodsRow.packaging = item.costofGoods
								pmiRow.packaging = item.total
								//calculate percentage
								if (item.climateChange != null && item.climateChange > 0 && climateChangeTotal != null && climateChangeTotal > 0) {
									climateChangePercentage = item.climateChange / climateChangeTotal
									// if(climateChangePercentage != null && climateChangePercentage > 14)
									// {
									// 	climateChangePercentage = 14
									// }
								}
								if (item.waterConsumption != null && item.waterConsumption > 0 && waterConsumptionTotal != null && waterConsumptionTotal > 0) {
									waterConsumptionPercentage = item.waterConsumption / waterConsumptionTotal
									// if(waterConsumptionPercentage != null && waterConsumptionPercentage > 14)
									// {
									// 	waterConsumptionPercentage = 14
									// }
								}
								if (item.costofGoods != null && item.costofGoods > 0 && costOfGoodsTotal != null && costOfGoodsTotal > 0) {
									costOfGoodsPercentage = item.costofGoods / costOfGoodsTotal
									// if(costOfGoodsPercentage != null && costOfGoodsPercentage > 14)
									// {
									// 	costOfGoodsPercentage = 14
									// }
								}
								if (item.total != null && item.total > 0 && pmiTotal != null && pmiTotal > 0) {
									pmiPercentage = item.total / pmiTotal
									// if(pmiPercentage != null && pmiPercentage > 14)
									// {
									// 	pmiPercentage = 14
									// }
								}
							} else if (item.name == "Distribution") {
								let climateChangePercentage = 0
								let waterConsumptionPercentage = 0
								let costOfGoodsPercentage = 0
								let pmiPercentage = 0

								climateChangeRow.distribution = item.climateChange
								waterConsumptionRow.distribution = item.waterConsumption
								costOfGoodsRow.distribution = item.costofGoods
								pmiRow.distribution = item.total
								//calculate percentage
								if (item.climateChange != null && item.climateChange > 0 && climateChangeTotal != null && climateChangeTotal > 0) {
									climateChangePercentage = item.climateChange / climateChangeTotal
									// if(climateChangePercentage != null && climateChangePercentage > 14)
									// {
									// 	climateChangePercentage = 14
									// }
								}
								if (item.waterConsumption != null && item.waterConsumption > 0 && waterConsumptionTotal != null && waterConsumptionTotal > 0) {
									waterConsumptionPercentage = item.waterConsumption / waterConsumptionTotal
									// if(waterConsumptionPercentage != null && waterConsumptionPercentage > 14)
									// {
									// 	waterConsumptionPercentage = 14
									// }
								}
								if (item.costofGoods != null && item.costofGoods > 0 && costOfGoodsTotal != null && costOfGoodsTotal > 0) {
									costOfGoodsPercentage = item.costofGoods / costOfGoodsTotal
									// if(costOfGoodsPercentage != null && costOfGoodsPercentage > 14)
									// {
									// 	costOfGoodsPercentage = 14
									// }
								}
								if (item.total != null && item.total > 0 && pmiTotal != null && pmiTotal > 0) {
									pmiPercentage = item.total / pmiTotal
									// if(pmiPercentage != null && pmiPercentage > 14)
									// {
									// 	pmiPercentage = 14
									// }
								}
							} else if (item.name == "Packaging (EoL)") {
								let climateChangePercentage = 0
								let waterConsumptionPercentage = 0
								let costOfGoodsPercentage = 0
								let pmiPercentage = 0

								climateChangeRow.packagingEol = item.climateChange
								waterConsumptionRow.packagingEol = item.waterConsumption
								costOfGoodsRow.packagingEol = item.costofGoods
								pmiRow.packagingEol = item.total
								//calculate percentage
								if (item.climateChange != null && item.climateChange > 0 && climateChangeTotal != null && climateChangeTotal > 0) {
									climateChangePercentage = item.climateChange / climateChangeTotal
									// if(climateChangePercentage != null && climateChangePercentage > 14)
									// {
									// 	climateChangePercentage = 14
									// }
								}
								if (item.waterConsumption != null && item.waterConsumption > 0 && waterConsumptionTotal != null && waterConsumptionTotal > 0) {
									waterConsumptionPercentage = item.waterConsumption / waterConsumptionTotal
									// if(waterConsumptionPercentage != null && waterConsumptionPercentage > 14)
									// {
									// 	waterConsumptionPercentage = 14
									// }
								}
								if (item.costofGoods != null && item.costofGoods > 0 && costOfGoodsTotal != null && costOfGoodsTotal > 0) {
									costOfGoodsPercentage = item.costofGoods / costOfGoodsTotal
									// if(costOfGoodsPercentage != null && costOfGoodsPercentage > 14)
									// {
									// 	costOfGoodsPercentage = 14
									// }
								}
								if (item.total != null && item.total > 0 && pmiTotal != null && pmiTotal > 0) {
									pmiPercentage = item.total / pmiTotal
									// if(pmiPercentage != null && pmiPercentage > 14)
									// {
									// 	pmiPercentage = 14
									// }
								}
							}
						})

						lifeCycleResultListGraph1.value.push(climateChangeRow)
						lifeCycleResultListGraph1.value.push(waterConsumptionRow)
						lifeCycleResultListGraph1.value.push(costOfGoodsRow)
						lifeCycleResultListGraph1.value.push(pmiRow)
						displayLifeCycleStage.value = true
						displayLifeCycleResultListGraph1.value = true

						forceRerender()
					} else {
						console.log("No data received or empty response:", res)
					}
				},
				(err) => {
					//67441
					console.log("API Error:", err)
					displayCalculationRunning.value = false
					useErrorToaster(err)
				}
			)
			.then(() => {
				EventBus.emit("readyToSaveResult_" + props.tabId)
			})
	}
}

function changePage(action: string) {}

function formatDecimal(num: number) {
	return valueToStringFormatter(num)
}

function forceRerender() {
	resultKey.value += 1
}

function numbersOnly(evt: Event) {
	const keyboardEvent = evt as KeyboardEvent
	const target = keyboardEvent.target as HTMLInputElement
	const charCode = keyboardEvent.which ? keyboardEvent.which : keyboardEvent.keyCode

	if (
		(charCode != 46 || target.value.indexOf(".") != -1) && // "." CHECK DOT, AND ONLY ONE.
		(charCode < 48 || charCode > 57)
	) {
		keyboardEvent.preventDefault()
		return false
	} else {
		return true
	}
}

async function loadFeatureFlag() {
	try {
		const flag = await featureFlagClient.getByName("Automatic Population of Lifecycle Quantity")
		isAutomaticPopulationEnabled.value = flag?.isEnabled ?? false

		if (isAutomaticPopulationEnabled.value) {
			automaticPopulationWarnings.value = ["Drug product mass and unit are now automatically populated based on the Product Format, Mass per Product Format, and Unit fields in General Information of the 'Product Process' module."]
		}

		// Load multi-option feature flag
		const multiOptionFlag = await featureFlagClient.getByName("Allow Multiple Packaging Options per Assessment")
		isMultiOptionFeatureEnabled.value = multiOptionFlag?.isEnabled ?? false
	} catch (err) {
		console.warn("Could not load feature flag:", err)
		isAutomaticPopulationEnabled.value = false
		isMultiOptionFeatureEnabled.value = false
	}
}

async function checkSubprojectHasProductProcess() {
	if (!props.assessmentId) return

	try {
		const result = await apiClient.checkIfSubprojectHasProductProcessScreen(props.assessmentId)
		hasProductProcessTab.value = result
		console.log("Product Process tab exists:", hasProductProcessTab.value)
	} catch (error) {
		console.error("Error checking if subproject has Product Process screen:", error)
		hasProductProcessTab.value = false
	}
}

function loadBasicInfo() {
	apiClient.getCommonAPISingleGeneralByAssessmentIdAndScreenIdPP(props.assessmentId).then((res) => {

		if (res && isAutomaticPopulationEnabled.value) {
			// Check if productFormatId, massPerProductFormat and massPerProductFormatUnitId are present
			if (res.productFormatId && res.massPerProductFormat && res.massPerProductFormatUnitId) {
				// Load values from database
				drugProductMassPerReferenceUnit.value = res.massPerProductFormat
				selectedUnitTypeName.value = res.massPerProductFormatUnit?.unitName ?? ""
				selectedUnitType.value = res.massPerProductFormatUnit?.id ?? ""
				isProductFormatDataMissing.value = false
				productFormatName.value = res.productFormat?.formatName
			} else {
				// Values are missing - mark as missing and show warning
				isProductFormatDataMissing.value = true

				// Clear values - they won't be displayed anyway
				drugProductMassPerReferenceUnit.value = null
				selectedUnitTypeName.value = ""
				selectedUnitType.value = ""

				// Update warning message - conditional based on Product Process existence
				const moduleText = hasProductProcessTab.value
					? "'Product Process' module"
					: "'Device and Packaging' module"

				automaticPopulationWarnings.value = [
					`Calculation cannot proceed until the Product Format, Mass per Product Format, and Unit fields are completed in General Information of ${moduleText}.`
				]
			}
		}
	})
}

function getAllUnit() {
	apiClient.getUnitByUnitTypeName("Weight").then((res: any) => {
		unitList.value = res
	})
}

function validateFields() {
	// Skip validation if automatic population is enabled - values are auto-populated
	if (isAutomaticPopulationEnabled.value) {
		return true
	}

	if (
		drugProductMassPerReferenceUnit.value == null ||
		drugProductMassPerReferenceUnit.value == 0 ||
		selectedUnitType.value == null ||
		selectedUnitType.value == "" ||
		selectedUnitType.value == "Select Unit"
	) {
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "You must complete the highlighted fields",
			life: 2500,
		})
		searchAttempted.value = true
		return false
	}

	return true
}

function validateResultMatrix() {
	var result = false
	let obj = new LifeCycleSummaryResultInput()
	obj.assessmentId = props.assessmentId
	obj.unitValue = drugProductMassPerReferenceUnit.value ?? undefined
	obj.unitType = selectedUnitType.value

	if (obj != null) {
		apiClient.validateLifecycleSummaryResultMatrix(obj).then(
			(warnings) => {
				//console.log("validateResultMatrix result", res)
				displayValidationRunning.value = false
				displayCalculationRunning.value = true
				warningMessages.value = warnings
				getLifeCycleSummaryResult()
			},
			(err) => {
				displayValidationRunning.value = false
				displayCalculationRunning.value = false
				if (err.status == 400) {
					//toast.add({ severity: "error", summary: `Error: ${err.response.message}`, life: 5000 })
					//displayResult.value = false
					console.error("Error Id:", err.response.errorId)
					warningMessages.value = [err.response.message]
				} else {
					toast.add({ severity: "error", summary: `Error: ${err}` })
				}
			}
		)
	}
	return result
}

function generateLifeCycleSummaryResult() {
	// Populate from doseStrength if automatic population is enabled
	if (isAutomaticPopulationEnabled.value) {
		const parsed = parsedDoseStrength.value
		if (parsed.value && parsed.unit) {
			drugProductMassPerReferenceUnit.value = parsed.value
			selectedUnitType.value = parsed.unit
		}
	}

	console.log(
		"generateLifeCycleSummaryResult called",
		drugProductMassPerReferenceUnit.value,
		selectedUnitType.value,
		isAutomaticPopulationEnabled.value
	)

	if (displayUnit.value === true && !validateFields()) return
	searchAttempted.value = true
	displayValidationRunning.value = true

	validateResultMatrix()
	//getLifeCycleSummaryResult()
}

// Watch for changes to isWeighted toggle and recalculate if results are displayed
watch(
	isWeighted,
	() => {
		if (displayLifeCycleStage.value) {
			getLifeCycleSummaryResult()
		}
	},
	{ flush: "post" }
)
</script>
<style>
.p-invalid {
	border-color: red !important;
}
.my-warning-icon::before {
	font-family: "Font Awesome 6 Pro";
	font-weight: 400;
	content: "\f071";

	color: var(--yellow-600, #d6ad03);
	margin-right: 0.5rem;
	vertical-align: middle;
	font-size: 3rem;
	display: inline-block;
}

.text-yellow-600-custom {
	color: var(--yellow-600, #d6ad03);
}

.custom-tooltip-wide {
	max-width: 400px !important;
}
</style>
